// =============================================================================
// web/shims/react-native-video.js — WEB shim for react-native-video.
//
// react-native-video is a native player (AVPlayer/ExoPlayer). On web we back it
// with a plain HTML5 <video> element so feed/story/channel video still PLAYS in
// the browser, mapping the props the screens use (source.uri, paused, repeat,
// muted, resizeMode, style, poster). Native iOS resolves the real package.
// =============================================================================
import React from 'react';

const RESIZE_TO_OBJECT_FIT = {
  cover: 'cover',
  contain: 'contain',
  stretch: 'fill',
  none: 'none',
  center: 'contain',
};

const Video = React.forwardRef(function VideoShim(props, ref) {
  const {
    source,
    paused,
    repeat,
    muted,
    volume,
    resizeMode,
    poster,
    style,
    onLoad,
    onEnd,
    onError,
    onProgress,
    controls,
    ...rest
  } = props || {};

  const uri = source && (source.uri || (typeof source === 'string' ? source : undefined));
  const videoRef = React.useRef(null);

  React.useImperativeHandle(ref, () => ({
    seek: (t) => {
      if (videoRef.current) {
        try {
          videoRef.current.currentTime = t;
        } catch {}
      }
    },
    presentFullscreenPlayer: () => {},
    dismissFullscreenPlayer: () => {},
  }));

  React.useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (paused) {
      try {
        el.pause();
      } catch {}
    } else {
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  }, [paused, uri]);

  // Flatten RN style objects into a DOM style as best-effort.
  const flatStyle = Array.isArray(style) ? Object.assign({}, ...style.filter(Boolean)) : style || {};

  return React.createElement('video', {
    ref: videoRef,
    src: uri,
    poster: typeof poster === 'string' ? poster : poster && poster.source && poster.source.uri,
    loop: !!repeat,
    muted: !!muted,
    controls: !!controls,
    playsInline: true,
    autoPlay: !paused,
    onLoadedData: onLoad,
    onEnded: onEnd,
    onError,
    onTimeUpdate: onProgress
      ? (e) =>
          onProgress({
            currentTime: e.target.currentTime,
            playableDuration: e.target.duration,
            seekableDuration: e.target.duration,
          })
      : undefined,
    style: {
      objectFit: RESIZE_TO_OBJECT_FIT[resizeMode] || 'cover',
      ...flatStyle,
    },
    ...rest,
  });
});

export default Video;
